import { useEffect, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import usePageTitle from "@/hooks/use-page-title";
import OrderItemsEditor, { emptyItem, calcItemTotals, calcExtra } from "@/components/OrderItemsEditor";
import CatalogPicker from "@/components/CatalogPicker";
import ExtrasEditor from "@/components/ExtrasEditor";

export default function NewQuotation() {
  usePageTitle("New Quotation");
  const nav = useNavigate();
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [err, setErr] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    customer_id: "", quote_date: today, valid_till: "",
    transport_charge: 0, transport_gst_rate: 18, transport_gst_mode: "none",
    installation_charge: 0, installation_gst_rate: 18, installation_gst_mode: "none",
    discount_amount: 0,
    remarks: "", shipping_address: "",
  });
  const [items, setItems] = useState([]);

  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)); }, []);
  if (user?.role === "customer") return <Navigate to="/portal" replace />;

  const set = (k, v) => setForm({ ...form, [k]: v });
  const sub = items.reduce((s, it) => s + calcItemTotals(it).subtotal, 0);
  const tax = items.reduce((s, it) => s + calcItemTotals(it).tax, 0);
  const tExtra = calcExtra(form.transport_charge, form.transport_gst_rate, form.transport_gst_mode);
  const iExtra = calcExtra(form.installation_charge, form.installation_gst_rate, form.installation_gst_mode);
  const extras = tExtra.total + iExtra.total;
  const grand = Math.max(sub + tax + extras - Number(form.discount_amount || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.customer_id) { setErr("Select a customer"); return; }
    try {
      const payload = {
        ...form,
        items: items.map((it) => ({
          ...it,
          quantity: Number(it.quantity) || 1,
          gst_rate: Number(it.gst_rate) || 0,
          gst_mode: it.gst_mode || "excluded",
          sizes: (it.sizes || []).map((s) => ({
            width: Number(s.width) || 0, height: Number(s.height) || 0,
            unit: s.unit || "ft", quantity: Number(s.quantity) || 1, rate: Number(s.rate) || 0,
          })),
          addons: (it.addons || []).map((a) => ({
            name: a.name, quantity: Number(a.quantity) || 1, rate: Number(a.rate) || 0,
          })),
        })),
        transport_charge: Number(form.transport_charge) || 0,
        transport_gst_rate: Number(form.transport_gst_rate) || 0,
        installation_charge: Number(form.installation_charge) || 0,
        installation_gst_rate: Number(form.installation_gst_rate) || 0,
        discount_amount: Number(form.discount_amount) || 0,
      };
      const { data } = await api.post("/quotations", payload);
      nav(`/quotations/${data.id}`);
    } catch (e2) { setErr(formatApiError(e2)); }
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="new-quotation-root">
      <Link to="/quotations" className="text-sm text-muted-foreground hover:text-[#FF6B00] inline-flex items-center gap-2">
        <ArrowLeft size={14} /> Quotations
      </Link>
      <div className="border-b border-[hsl(var(--border))] pb-6">
        <div className="overline mb-2">Create</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">New Quotation</h1>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <div className="card-swiss p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-3">
            <label className="overline block mb-2">Customer *</label>
            <select required value={form.customer_id} onChange={(e) => set("customer_id", e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none" data-testid="quote-customer-select">
              <option value="">Select customer…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name} {c.company ? `— ${c.company}` : ""} · {c.phone}</option>)}
            </select>
          </div>
          <div>
            <label className="overline block mb-2">Quote Date</label>
            <input type="date" value={form.quote_date} onChange={(e) => set("quote_date", e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none" data-testid="quote-date-input" />
          </div>
          <div>
            <label className="overline block mb-2">Valid Till</label>
            <input type="date" value={form.valid_till} onChange={(e) => set("valid_till", e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none" data-testid="quote-valid-input" />
          </div>
        </div>

        <div className="card-swiss p-5 space-y-4">
          <div className="overline">Products</div>
          <CatalogPicker
            onPick={(item) => { setItems((p) => [...p, item]); toast.success(`${item.product_name.split(" · ")[0]} added — auto-filled from catalog`); }}
            onCustom={() => { setItems((p) => [...p, emptyItem()]); toast.success("Custom product added"); }}
          />
          {items.length > 0 && <OrderItemsEditor items={items} setItems={setItems} testIdPrefix="quote-item" />}
          {items.length === 0 && (
            <div className="p-6 border-2 border-dashed border-zinc-200 rounded-xl text-center text-sm text-zinc-500">
              Search the catalog above, browse the grid, or add a custom product to start the quotation.
            </div>
          )}
        </div>

        <div className="card-swiss p-5 space-y-4">
          <div className="overline">Extras & Their GST</div>
          <ExtrasEditor form={form} set={(patch) => setForm({ ...form, ...patch })} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-3 border-t border-[hsl(var(--border))]">
            <div>
              <label className="overline block mb-2">Discount (₹)</label>
              <input type="number" step="0.01" value={form.discount_amount} onChange={(e) => set("discount_amount", e.target.value)}
                className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none" data-testid="quote-discount-input" />
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-2">Shipping Address</label>
              <textarea rows={2} value={form.shipping_address} onChange={(e) => set("shipping_address", e.target.value)}
                className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none" data-testid="quote-shipping-address" />
            </div>
          </div>
          <div>
            <label className="overline block mb-2">Remarks / Terms</label>
            <textarea rows={3} value={form.remarks} onChange={(e) => set("remarks", e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none" data-testid="quote-remarks-input" />
          </div>
        </div>

        <div className="card-swiss p-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><div className="overline">Subtotal</div><div className="font-mono">₹{Math.round(sub).toLocaleString("en-IN")}</div></div>
          <div><div className="overline">GST</div><div className="font-mono">₹{Math.round(tax + tExtra.tax + iExtra.tax).toLocaleString("en-IN")}</div></div>
          <div><div className="overline">Extras / Disc.</div><div className="font-mono">₹{Math.round(tExtra.base + iExtra.base - Number(form.discount_amount || 0)).toLocaleString("en-IN")}</div></div>
          <div><div className="overline text-[#FF6B00]">Grand Total</div><div className="font-mono font-black text-lg text-[#FF6B00]" data-testid="quote-grand-total">₹{Math.round(grand).toLocaleString("en-IN")}</div></div>
        </div>

        {err && <div className="text-sm text-red-600" data-testid="quote-form-error">{err}</div>}

        <div className="flex justify-end gap-3 pt-2">
          <Link to="/quotations" className="btn-swiss btn-outline">Cancel</Link>
          <button type="submit" className="btn-swiss btn-accent" data-testid="submit-quote-btn">Save Quotation (Draft)</button>
        </div>
      </form>
    </div>
  );
}
